import { create } from 'zustand';
import {
  TruckSchema,
  TruckRequest,
  UpdateTruckRequest,
  TruckQueryParams,
} from '../types/transportation.type';
import * as transportationService from '../services/transportation.service';

interface TruckState {
  trucks: TruckSchema[];
  trucksLoading: boolean;
  trucksError: string | null;
  totalTrucks: number;
  trucksPage: number;
  trucksHasMore: boolean;
  trucksTotalPages: number;

  selectedTruck: TruckSchema | null;
  selectedTruckLoading: boolean;
  selectedTruckError: string | null;
  selectedTruckNotFound: boolean;

  fetchTrucks: (params: TruckQueryParams) => Promise<void>;
  fetchTruck: (organizationId: string, truckId: string) => Promise<TruckSchema | null>;
  createTruck: (data: TruckRequest) => Promise<TruckSchema | null>;
  updateTruck: (id: string, data: UpdateTruckRequest) => Promise<TruckSchema | null>;
  deleteTruck: (id: string) => Promise<boolean>;
  setSelectedTruck: (item: TruckSchema | null) => void;
  clearTrucksError: () => void;
  clearSelectedTruckError: () => void;
}

interface GenericResponse<T> {
  result: T;
}

function errorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: string | { message?: string } } } };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
}

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } }).response?.status === 404;
}

export const useTruckStore = create<TruckState>((set) => ({
  trucks: [],
  trucksLoading: false,
  trucksError: null,
  totalTrucks: 0,
  trucksPage: 1,
  trucksHasMore: false,
  trucksTotalPages: 0,

  selectedTruck: null,
  selectedTruckLoading: false,
  selectedTruckError: null,
  selectedTruckNotFound: false,

  fetchTrucks: async (params) => {
    try {
      set({ trucksLoading: true, trucksError: null });
      const response = await transportationService.getTrucks(params);
      // Handle both response formats: { success, data } or { result }
      const data = response as GenericResponse<TruckSchema[]> & { success?: boolean; data?: TruckSchema[] };
      const trucksList = data.result || data.data || [];
      set({
        trucks: trucksList,
        totalTrucks: trucksList.length,
        trucksPage: 1,
        trucksHasMore: false,
        trucksTotalPages: 1,
        trucksLoading: false,
      });
    } catch (error) {
      set({
        trucksError: error instanceof Error ? error.message : 'Failed to fetch trucks',
        trucksLoading: false,
      });
    }
  },

  fetchTruck: async (_organizationId, truckId) => {
    set({ selectedTruckLoading: true, selectedTruckError: null, selectedTruckNotFound: false });
    try {
      const response = await transportationService.getTruck(truckId);
      const truck = (response as GenericResponse<TruckSchema>).result;
      set({ selectedTruck: truck, selectedTruckLoading: false, selectedTruckNotFound: false });
      return truck;
    } catch (error) {
      if (isNotFound(error)) {
        set({
          selectedTruck: null,
          selectedTruckLoading: false,
          selectedTruckError: null,
          selectedTruckNotFound: true,
        });
        return null;
      }
      set({ selectedTruckLoading: false, selectedTruckError: errorMessage(error, 'Failed to fetch truck') });
      return null;
    }
  },

  createTruck: async (data) => {
    set({ trucksLoading: true, trucksError: null });
    try {
      const response = await transportationService.createTruck(data);
      const truck = (response as GenericResponse<TruckSchema>).result;
      set((state) => ({
        trucks: [truck, ...state.trucks],
        totalTrucks: state.totalTrucks + 1,
        trucksLoading: false,
      }));
      return truck;
    } catch (error) {
      set({ trucksLoading: false, trucksError: errorMessage(error, 'Failed to create truck') });
      return null;
    }
  },

  updateTruck: async (id, data) => {
    set({ trucksLoading: true, trucksError: null });
    try {
      const response = await transportationService.updateTruck(id, data);
      const truck = (response as GenericResponse<TruckSchema>).result;
      set((state) => ({
        trucks: state.trucks.map((row) => (row.id === id ? truck : row)),
        selectedTruck: state.selectedTruck?.id === id ? truck : state.selectedTruck,
        trucksLoading: false,
      }));
      return truck;
    } catch (error) {
      set({ trucksLoading: false, trucksError: errorMessage(error, 'Failed to update truck') });
      return null;
    }
  },

  deleteTruck: async (id) => {
    set({ trucksLoading: true, trucksError: null });
    try {
      await transportationService.deleteTruck(id);
      set((state) => ({
        trucks: state.trucks.filter((row) => row.id !== id),
        totalTrucks: Math.max(0, state.totalTrucks - 1),
        selectedTruck: state.selectedTruck?.id === id ? null : state.selectedTruck,
        trucksLoading: false,
      }));
      return true;
    } catch (error) {
      set({ trucksLoading: false, trucksError: errorMessage(error, 'Failed to delete truck') });
      return false;
    }
  },

  setSelectedTruck: (item) => {
    set({ selectedTruck: item });
  },

  clearTrucksError: () => {
    set({ trucksError: null });
  },

  clearSelectedTruckError: () => {
    set({ selectedTruckError: null });
  },
}));
